import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Info, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { TEMPLATE_TINT_CLASSES, type DocumentTemplate } from "../constants/documentTemplates";
import {
  buildTemplateSections,
  filterTemplateSections,
  findTemplateItem,
  moveHighlight,
  selectableTemplateKeys,
  type TemplateCardItem,
} from "../utils/templateSections";

interface NewDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The single extension point — the template form view hangs off this. */
  onSelectTemplate: (template: DocumentTemplate) => void;
  /** The button that opened the modal; focus returns to it on close. Radix's
   *  own restore misses it because the toolbar renders through a portal and
   *  re-renders while the modal is open, detaching the node it remembered. */
  triggerRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * "New document" — pick a template to create a document from.
 *
 * Keyboard: ↑/↓ move the highlight across the flattened, filtered list while
 * focus stays in the search field, Enter creates from the highlighted card,
 * Esc closes (Radix). The highlight is exposed with `aria-activedescendant`
 * over a listbox of real buttons, so pointer and keyboard agree.
 */
export function NewDocumentModal({
  open,
  onOpenChange,
  onSelectTemplate,
  triggerRef,
}: NewDocumentModalProps) {
  const { t } = useLanguage();
  const copy = t.documents.newDocument;
  const listId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Set when the close is a handoff to the template form dialog — then the
  // form takes focus, and restoring the trigger would fight its focus scope.
  const handedOffRef = useRef(false);
  // Only keyboard-driven highlight changes scroll the list. Scrolling on hover
  // would move a half-visible card under the stationary cursor, firing another
  // mouseenter — the list would chase the pointer.
  const viaKeyboardRef = useRef(false);

  const [query, setQuery] = useState("");
  const [highlightKey, setHighlightKey] = useState<string | null>(null);

  const sections = useMemo(() => buildTemplateSections(copy), [copy]);
  const visibleSections = useMemo(() => filterTemplateSections(sections, query), [sections, query]);
  const selectableKeys = useMemo(() => selectableTemplateKeys(visibleSections), [visibleSections]);

  // Derived, not stored: filtering can drop the highlighted card, and the first
  // remaining card takes over. Keeps the highlight valid without an effect.
  const highlighted =
    highlightKey && selectableKeys.includes(highlightKey)
      ? highlightKey
      : (selectableKeys[0] ?? null);

  useEffect(() => {
    if (!viaKeyboardRef.current) return;
    viaKeyboardRef.current = false;
    if (highlighted) cardRefs.current[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const optionId = useCallback((key: string) => `${listId}-${key.replace(":", "-")}`, [listId]);

  const handleSelect = useCallback(
    (item: TemplateCardItem) => {
      if (item.template.disabled) return;
      handedOffRef.current = true;
      onSelectTemplate(item.template);
      onOpenChange(false);
    },
    [onSelectTemplate, onOpenChange]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // During IME composition (CJK input) Enter confirms the composed text and
    // arrows navigate the candidate list — leave both to the IME.
    if (event.nativeEvent.isComposing) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      viaKeyboardRef.current = true;
      setHighlightKey(
        moveHighlight(selectableKeys, highlighted, event.key === "ArrowDown" ? 1 : -1)
      );
      return;
    }
    if (event.key === "Enter" && event.target === searchRef.current) {
      const item = findTemplateItem(visibleSections, highlighted);
      if (item) {
        event.preventDefault();
        handleSelect(item);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl gap-0 p-0"
        // The modal is a template picker, not a prose dialog: the search field
        // and the listbox label carry the meaning, so there is no description.
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          // Each visit starts clean, with the caret in the search field.
          event.preventDefault();
          setQuery("");
          setHighlightKey(null);
          searchRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          if (handedOffRef.current) {
            handedOffRef.current = false;
            event.preventDefault();
            return;
          }
          if (!triggerRef?.current) return;
          event.preventDefault();
          triggerRef.current.focus();
        }}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="px-6 pb-4 pt-6">
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-4">
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={copy.searchPlaceholder}
              className="pl-9"
              role="combobox"
              aria-expanded
              aria-autocomplete="list"
              aria-controls={listId}
              aria-activedescendant={highlighted ? optionId(highlighted) : undefined}
            />
          </div>
        </div>

        <div
          id={listId}
          role="listbox"
          aria-label={copy.listLabel}
          className="max-h-[60vh] space-y-6 overflow-y-auto border-t px-6 py-5"
        >
          {visibleSections.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm font-medium">{copy.noResults}</p>
              <p className="mt-1 text-sm text-muted-foreground">{copy.noResultsHint}</p>
            </div>
          ) : (
            visibleSections.map((section) => (
              <section key={section.id} role="group" aria-labelledby={`${listId}-${section.id}`}>
                <h3
                  id={`${listId}-${section.id}`}
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {section.title}
                </h3>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {section.items.map((item) => (
                    <TemplateCard
                      key={item.key}
                      ref={(node) => {
                        cardRefs.current[item.key] = node;
                      }}
                      id={optionId(item.key)}
                      item={item}
                      aboutLabel={copy.aboutTemplate}
                      highlighted={item.key === highlighted}
                      onSelect={() => handleSelect(item)}
                      onHover={() => !item.template.disabled && setHighlightKey(item.key)}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TemplateCardProps {
  id: string;
  item: TemplateCardItem;
  /** Accessible name for the ⓘ, which shows no text of its own. */
  aboutLabel: string;
  highlighted: boolean;
  onSelect: () => void;
  onHover: () => void;
}

const TemplateCard = forwardRef<HTMLButtonElement, TemplateCardProps>(function TemplateCard(
  { id, item, aboutLabel, highlighted, onSelect, onHover },
  ref
) {
  const Icon = item.template.icon;
  const disabled = Boolean(item.template.disabled);

  const card = (
    <button
      ref={ref}
      id={id}
      type="button"
      role="option"
      aria-selected={highlighted}
      disabled={disabled}
      onClick={onSelect}
      onMouseEnter={onHover}
      className={cn(
        "flex h-full w-full items-start gap-3 rounded-lg border p-3 pr-9 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        disabled && "cursor-default opacity-60",
        // The highlight is the border alone — no fill. A grey wash under the
        // pointer read as a disabled state rather than a hover, and the border
        // is what ↑/↓ needs to be legible anyway.
        highlighted && !disabled ? "border-primary" : "border-border"
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
          TEMPLATE_TINT_CLASSES[item.template.tint]
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-snug">{item.name}</span>
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
          {item.description}
        </span>
      </span>
    </button>
  );

  return (
    // h-full on both: the grid stretches this wrapper to the tallest card in
    // the row, and the button has to fill it or the bordered boxes come out
    // ragged where one description runs longer than its neighbour's.
    <div className="relative h-full">
      {/* The bilingual name lives in a tooltip rather than on the card: these
          are Estonian statutory documents, so the card shows the reader's
          language and hovering discloses the name it is actually filed under.
          Radix adds only `aria-describedby` to the trigger, so the listbox's
          `aria-activedescendant` wiring is untouched. */}
      {item.fullName === item.name ? (
        card
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>{card}</TooltipTrigger>
          <TooltipContent>{item.fullName}</TooltipContent>
        </Tooltip>
      )}

      {/* Outside the card button, not inside it: a button within a button is
          invalid, and an interactive child would muddle the listbox option.
          A "coming soon" placeholder gets none — there is nothing to explain
          about a card that cannot be opened. */}
      {!disabled && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={aboutLabel}
              // Picking the template is the card's job — the ⓘ only explains.
              onClick={(event) => event.stopPropagation()}
              className="absolute right-2 top-2 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{item.details}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
});
